// Сбор событий витрины: показы карточек в выдаче, открытия карточки товара,
// добавления в корзину. Из них шлюз считает воронку продавца («Показы →
// Просмотр карточки → Добавление в корзину → Оформление → Покупка») и
// просмотры в аналитике товара. До этого модуля воронка была слепа на первых
// трёх ступенях: ручка на шлюзе была, писать в неё было некому.
//
// Три правила, которые здесь важнее удобства:
//
// 1. НЕ ТОРМОЗИТЬ ВИТРИНУ. События копятся в очереди и уходят пачкой; ни один
//    вызов ничего не ждёт и никогда не бросает. Потерять хвост очереди при
//    закрытии вкладки допустимо, задержать прокрутку — нет.
// 2. ОДИН ПОКАЗ НА КАРТОЧКУ ЗА СЕССИЮ. Без дедупликации прокрутка каталога
//    туда-обратно накрутила бы показы в разы, и конверсия «показ → просмотр»
//    поехала бы вниз на ровном месте.
// 3. ПОКАЗ — ЭТО ПОЯВЛЕНИЕ В ОКНЕ ПРОСМОТРА, А НЕ РЕНДЕР. Карточка, которую
//    пролистали не глядя, показом не считается: нужен порог видимости и
//    выдержка по времени.
import { useCallback } from "react";

import { api } from "@/api";
import type { DiscoveryEventInput } from "@/api";
import { useWishlist } from "@/store";

import { type IdeaOrigin, ideaOriginFor } from "./idea-origin";
import { readUgcArticleCode } from "./ugc-attribution";

/** Сколько событий копим до принудительного сброса очереди. */
const BATCH_SIZE = 20;

/** Пауза без новых событий, после которой очередь уходит сама. */
const FLUSH_DELAY_MS = 2_000;

/** Доля карточки в окне просмотра, с которой начинается отсчёт показа. */
const IMPRESSION_RATIO = 0.5;

/**
 * Сколько карточка должна провисеть в окне просмотра, чтобы это был показ.
 *
 * Быстрая прокрутка протаскивает через экран десятки карточек за секунду; без
 * выдержки все они стали бы «показами», и первая ступень воронки превратилась
 * бы в счётчик скорости пальца.
 */
const IMPRESSION_DWELL_MS = 500;

/** Ключ хранилища вкладки для дедупликации показов. */
const SEEN_KEY = "osmix-discovery-seen";

type QueuedEvent = DiscoveryEventInput;

let queue: QueuedEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let listenersBound = false;

const isBrowser = (): boolean => typeof window !== "undefined";

/**
 * Показы, уже отправленные в этой сессии.
 *
 * Живёт в `sessionStorage`, а не только в памяти: перезагрузка страницы в той
 * же вкладке — это та же сессия просмотра, и пересчитывать по ней показы
 * значит удваивать первую ступень воронки у всякого, кто нажал F5.
 */
const loadSeen = (): Set<string> => {
	if (!isBrowser()) return new Set();
	try {
		const raw = window.sessionStorage.getItem(SEEN_KEY);
		return new Set<string>(raw ? (JSON.parse(raw) as string[]) : []);
	} catch {
		// Приватный режим, заблокированное хранилище — дедупликация просто
		// останется в пределах жизни вкладки. Ронять витрину из-за телеметрии нельзя.
		return new Set();
	}
};

let seen: Set<string> | null = null;

const seenSet = (): Set<string> => {
	if (!seen) seen = loadSeen();
	return seen;
};

const rememberSeen = (key: string): void => {
	const set = seenSet();
	set.add(key);
	if (!isBrowser()) return;
	try {
		window.sessionStorage.setItem(SEEN_KEY, JSON.stringify([...set]));
	} catch {
		// см. loadSeen
	}
};

const clientId = (): string | undefined => {
	try {
		// Тот же анонимный идентификатор, что у гостевых лайков: у покупателя
		// на площадке одна анонимная личность, а не по одной на подсистему.
		return useWishlist.getState().clientId;
	} catch {
		return undefined;
	}
};

const flush = (): void => {
	if (flushTimer) {
		clearTimeout(flushTimer);
		flushTimer = null;
	}
	if (queue.length === 0) return;
	const events = queue;
	queue = [];
	// Осознанно без await и без обработки ошибок по существу: телеметрия не
	// имеет права ни задержать интерфейс, ни всплыть ошибкой в нём.
	void api.discovery.recordEvents({ clientId: clientId(), events }).catch(() => {});
};

const bindLifecycleListeners = (): void => {
	if (listenersBound || !isBrowser()) return;
	listenersBound = true;
	// `visibilitychange` на скрытие — единственное событие, которое надёжно
	// приходит перед выгрузкой вкладки и на мобильных тоже. `pagehide` добавлен
	// для Safari, где переход «назад» через bfcache не всегда даёт первое.
	const sendNow = () => flush();
	document.addEventListener("visibilitychange", () => {
		if (document.visibilityState === "hidden") sendNow();
	});
	window.addEventListener("pagehide", sendNow);
};

const enqueue = (event: QueuedEvent): void => {
	if (!isBrowser()) return;
	bindLifecycleListeners();
	queue.push(event);
	if (queue.length >= BATCH_SIZE) {
		flush();
		return;
	}
	if (flushTimer) clearTimeout(flushTimer);
	flushTimer = setTimeout(flush, FLUSH_DELAY_MS);
};

/**
 * Показ карточки товара в выдаче. Повторный показ той же карточки в пределах
 * сессии отбрасывается здесь, а не на сервере: дешевле не отправить.
 */
export const trackImpression = (productId: string): void => {
	if (!productId) return;
	const key = `i:${productId}`;
	if (seenSet().has(key)) return;
	rememberSeen(key);
	enqueue({ eventType: "product_impression", productId });
};

/**
 * Идея, из которой открыт товар, — в поля события. По `collectionId` шлюз
 * считает у идеи «переходы в карточку» и «в корзину»; без него оба столбца
 * метрик идеи всегда нули (см. `lib/idea-origin.tsx`).
 */
const withIdea = (productId: string, origin?: IdeaOrigin): Pick<QueuedEvent, "collectionId" | "collectionSlug"> => {
	const idea = origin ?? ideaOriginFor(productId);
	if (!idea?.collectionId) return {};
	return { collectionId: idea.collectionId, collectionSlug: idea.collectionSlug };
};

/**
 * Открыта страница товара (или быстрый просмотр внутри идеи). `metadata` -
 * доп. поля события: «Просмотр карточки по UGC-артикулу» уходит тем же
 * `product_view` с `{ source: "ugc_article", ugcArticleCode }` - словарь
 * типов событий на площадке один, новый тип шлюз бы отбросил.
 */
export const trackProductView = (productId: string, origin?: IdeaOrigin, metadata?: Record<string, unknown>): void => {
	if (!productId) return;
	enqueue({ eventType: "product_view", productId, ...withIdea(productId, origin), ...(metadata ? { metadata } : {}) });
};

/**
 * Товар положен в корзину.
 *
 * Дедупликации здесь нет намеренно: второе добавление того же товара — это
 * второе намерение купить, и слепить их в одно значило бы занизить ступень.
 */
export const trackCartAdd = (productId: string): void => {
	if (!productId) return;
	// Артикул с устройства (ТЗ 31.10.2026, п. 4.2) - в метаданные события корзины.
	const ugcArticleCode = readUgcArticleCode();
	enqueue({
		eventType: "cart_add",
		productId,
		...withIdea(productId),
		...(ugcArticleCode ? { metadata: { ugcArticleCode } } : {}),
	});
};

/**
 * Заказ оформлен (fd-discovery-06): по событию `order_create` на каждый товар
 * заказа, открытый из идеи, - с номером заказа и идеей. По ним шлюз относит
 * позицию к идее точно, а не по догадке из прошлых касаний (её он сделает сам
 * для товаров, о которых витрина не знает: второй заказ раздельного
 * оформления, другое устройство). Отправляется сразу, мимо очереди.
 */
export const trackOrderCreated = (orderId: string, productIds: string[]): void => {
	if (!orderId) return;
	for (const productId of new Set(productIds)) {
		const idea = ideaOriginFor(productId);
		if (!idea?.collectionId) continue;
		void api.discovery
			.recordOrderEvent({
				clientId: clientId(),
				orderId,
				productId,
				collectionId: idea.collectionId,
				...(idea.collectionSlug ? { collectionSlug: idea.collectionSlug } : {}),
			})
			.catch(() => {});
	}
};

/** Открыта подборка ленты. */
export const trackCollectionView = (slug: string, collectionId?: string): void => {
	if (!slug) return;
	enqueue({ eventType: "collection_view", collectionSlug: slug, collectionId });
};

/**
 * Наблюдатель показов — ОДИН на всю витрину.
 *
 * Отдельный `IntersectionObserver` на карточку стоил бы столько же объектов,
 * сколько карточек в выдаче; общий наблюдатель обходится одним. Карточка
 * снимается с наблюдения сразу после засчитанного показа — дальше следить за
 * ней не за чем.
 */
type Watched = { productId: string; timer: ReturnType<typeof setTimeout> | null };

const watched = new WeakMap<Element, Watched>();
let observer: IntersectionObserver | null = null;

const impressionObserver = (): IntersectionObserver | null => {
	if (!isBrowser() || typeof IntersectionObserver === "undefined") return null;
	if (observer) return observer;
	observer = new IntersectionObserver(
		(entries) => {
			for (const entry of entries) {
				const state = watched.get(entry.target);
				if (!state) continue;
				if (entry.isIntersecting) {
					if (state.timer) continue;
					state.timer = setTimeout(() => {
						state.timer = null;
						trackImpression(state.productId);
						observer?.unobserve(entry.target);
						watched.delete(entry.target);
					}, IMPRESSION_DWELL_MS);
				} else if (state.timer) {
					// Карточка ушла из окна раньше выдержки — показа не было.
					clearTimeout(state.timer);
					state.timer = null;
				}
			}
		},
		{ threshold: IMPRESSION_RATIO },
	);
	return observer;
};

/**
 * Ref для карточки товара в выдаче: подписывает её на наблюдение показов и
 * отписывает, когда карточка уходит из дерева.
 *
 * Ref, а не `useEffect`: карточек в сетке сотни, и эффект на каждую — лишняя
 * работа на каждой прокрутке. `useCallback` здесь обязателен: без него ссылка
 * менялась бы на каждый рендер, React отцеплял бы и прицеплял наблюдателя
 * заново, и выдержка показа обнулялась бы сама собой.
 */
export const useImpressionRef = (productId: string) =>
	useCallback(
		(element: HTMLElement | null) => {
			const io = impressionObserver();
			if (!io || !productId || !element) return;
			if (seenSet().has(`i:${productId}`)) return;
			watched.set(element, { productId, timer: null });
			io.observe(element);
			// React 19 зовёт этот колбэк при откреплении ref. Таймер обязательно
			// гасим: карточка, размонтированная в середине выдержки, показом не была.
			return () => {
				const state = watched.get(element);
				if (state?.timer) clearTimeout(state.timer);
				watched.delete(element);
				io.unobserve(element);
			};
		},
		[productId],
	);
